import { useEffect, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../lib/useClickOutside';
import { filterOptionsBySearch } from '../../lib/multiSelectSearch';

export type MultiSelectOption = {
  value: string;
  label: string;
  // Per-option disable (e.g. an engagement metric that doesn't apply to the
  // current channel). A disabled option can't be toggled and is skipped by
  // "Select all"; disabledTitle is its hover tooltip.
  disabled?: boolean;
  disabledTitle?: string;
};

type Props = {
  label: string;                 // e.g. "AMs"
  options: MultiSelectOption[];
  selected: string[];            // selected option values
  onChange: (next: string[]) => void;
  debounceMs?: number;           // default 250ms
  chipThreshold?: number;        // default 3 -- switch from listing to "N selected"
  ariaLabel?: string;
  placeholder?: string;          // text in trigger when nothing selected
  disabled?: boolean;            // grays out the trigger; dropdown can't open
  disabledTitle?: string;        // tooltip shown when disabled (e.g. why)
  // In-dropdown type-to-filter. Defaults ON when options.length > 8 so long
  // lists (Account/Brand/AM) "just work" without touching call sites; pass
  // `searchable={false}` to force it off on a short list.
  searchable?: boolean;
};

// Reusable multi-select with debounced emission. Displays:
//   - nothing selected -> "All {label}"
//   - ≤ chipThreshold   -> "{label}: A, B, C"
//   - > chipThreshold   -> "{label}: N selected"
//
// Click outside or Escape closes. The debounce coalesces rapid toggles into a
// single onChange so parent doesn't thrash. Local state reflects the latest
// toggle instantly so the UI stays responsive; onChange fires after debounceMs
// of silence.
export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  debounceMs = 250,
  chipThreshold = 3,
  ariaLabel,
  placeholder,
  disabled = false,
  disabledTitle,
  searchable,
}: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selected);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  // Auto-enable search for long lists; an explicit prop wins.
  const searchEnabled = searchable ?? options.length > 8;
  const visibleOptions = useMemo(
    () => (searchEnabled ? filterOptionsBySearch(options, search) : options),
    [searchEnabled, options, search],
  );

  // Reset the search box whenever the dropdown closes (and on open, so a stale
  // query never survives a reopen).
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // When selected changes from outside (parent reset, preset switch), rebaseline
  // the draft. Compare by stringified value -- sets are always small.
  useEffect(() => {
    setDraft(selected);
  }, [selected.join('|')]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced emission. Skip the first run (draft initialised from selected).
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    // If the draft matches the parent's current selection, skip the emit --
    // the change came from the parent baseline effect above.
    if (arraysEqualAsSets(draft, selected)) return;
    const handle = window.setTimeout(() => onChange(draft), debounceMs);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.join('|'), debounceMs]);

  useClickOutside([rootRef], () => setOpen(false), open);

  // Escape: with an active search, the first press clears the search; otherwise
  // it closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (searchEnabled && search !== '') setSearch('');
      else setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, searchEnabled, search]);

  const toggle = (value: string) => {
    setDraft((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };
  // "Select all" adds the currently-VISIBLE (filtered) non-disabled options to
  // the existing selection — it never clears options hidden by the search.
  const selectAll = () =>
    setDraft((prev) => {
      const next = new Set(prev);
      for (const o of visibleOptions) if (!o.disabled) next.add(o.value);
      return Array.from(next);
    });
  // "Clear" clears the ENTIRE selection, not just the visible options.
  const clearAll = () => setDraft([]);

  const triggerText = useMemo(() => {
    if (draft.length === 0) return placeholder ?? `All ${label}`;
    if (draft.length <= chipThreshold) {
      const optionMap = new Map(options.map((o) => [o.value, o.label]));
      const labels = draft.map((v) => optionMap.get(v) ?? v);
      return `${label}: ${labels.join(', ')}`;
    }
    return `${label}: ${draft.length} selected`;
  }, [draft, options, label, chipThreshold, placeholder]);

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-label={ariaLabel ?? label}
        aria-expanded={open}
        className="rounded border border-slate-300 px-2 py-1.5 text-sm bg-white hover:bg-slate-50 text-left min-w-[160px] max-w-[280px] truncate disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-slate-100"
        title={disabled ? (disabledTitle ?? triggerText) : triggerText}
      >
        {triggerText}
        <span className="ml-2 text-slate-400" aria-hidden>▾</span>
      </button>
      {open && !disabled && (
        <div
          role="listbox"
          aria-label={ariaLabel ?? label}
          className="absolute z-20 mt-1 w-64 max-h-72 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-lg p-2 text-sm"
        >
          {searchEnabled && (
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              aria-label={`Search ${ariaLabel ?? label}`}
              className="mb-2 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
          )}
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100">
            <button
              type="button"
              onClick={selectAll}
              className="text-xs text-slate-600 hover:text-slate-900 underline"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-slate-600 hover:text-slate-900 underline"
            >
              Clear
            </button>
          </div>
          {visibleOptions.length === 0 ? (
            <p className="text-xs text-slate-400 px-1 py-1">
              {options.length === 0 ? 'No options' : 'No matches'}
            </p>
          ) : (
            <ul className="space-y-0.5">
              {visibleOptions.map((o) => {
                const checked = draft.includes(o.value);
                return (
                  <li key={o.value}>
                    <label
                      className={`flex items-center gap-2 px-1 py-1 rounded ${
                        o.disabled
                          ? 'cursor-not-allowed text-slate-400'
                          : 'hover:bg-slate-50 cursor-pointer'
                      }`}
                      title={o.disabled ? (o.disabledTitle ?? o.label) : o.label}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={o.disabled}
                        onChange={() => toggle(o.value)}
                      />
                      <span className="truncate">{o.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function arraysEqualAsSets(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  for (const v of b) if (!sa.has(v)) return false;
  return true;
}
